import { useCanEdit } from '@/auth/account';
import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { CalendarRange, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import type { Investment } from '@/api/types';
import { useGroups, useInvestments, useSummary, useUpdateInvestment } from '@/api/hooks';
import { computeDrop, type CalendarDrag, type Range } from '@/lib/calendarDrop';
import { DraggableEntry, MonthColumn, PlanBar, UnscheduleZone } from '@/components/calendar/CalendarDnd';
import { MONTH_INITIALS, coversMonth, monthKey, monthsOfYear, yearOf } from '@/lib/date';
import { formatMoney, formatMoneyShort, formatMonth, formatMonthRange } from '@/lib/format';
import { chartColor, UNGROUPED_COLOR } from '@/lib/palette';
import { useIsDark } from '@/components/ThemeToggle';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { GroupBadge, StatusBadge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { BudgetMeter } from '@/components/charts/BudgetMeter';
import { InvestmentDialog } from '@/components/InvestmentDialog';
import { PageHeader, QueryState } from '@/components/PageHeader';

/** The day under the pointer wins – not the column most covered by the (wide) dragged bar. */
const dropUnderPointer: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  return hits.length > 0 ? hits : rectIntersection(args);
};

/**
 * One grid for the whole plan: a label column plus twelve month columns. The month
 * strips, the rows and the footer all use this template, so they line up exactly.
 */
const COLS = 'grid-cols-[minmax(8.5rem,1.15fr)_repeat(12,minmax(1.5rem,1fr))]';

interface Row {
  groupId: string | null;
  name: string;
  color: string;
  items: Investment[];
  total: number;
}

export function YearPlan() {
  const canEdit = useCanEdit();
  const groups = useGroups();
  const investments = useInvestments();
  const summary = useSummary();
  const isDark = useIsDark();

  // Computed per render, so the marker does not go stale in a tab left open.
  const thisMonth = monthKey(new Date());
  const [year, setYear] = useState(() => yearOf(thisMonth));
  const [dialogId, setDialogId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Drag & drop: new windows are shown immediately (optimistic) until the server has answered.
  const updateInvestment = useUpdateInvestment();
  const [overrides, setOverrides] = useState<Record<string, Range>>({});
  const [dragging, setDragging] = useState<{
    drag: CalendarDrag;
    title: string;
    group: string | null;
  } | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
  );

  const all = useMemo(
    () =>
      (investments.data ?? []).map((investment) => ({ ...investment, ...overrides[investment.id] })),
    [investments.data, overrides],
  );

  const currency = summary.data?.currency ?? 'EUR';
  const months = useMemo(() => monthsOfYear(year), [year]);
  const unscheduled = useMemo(() => all.filter((i) => i.plannedFrom === null), [all]);

  /** Everything whose window touches the shown year – those get a row. */
  const inYear = useMemo(
    () =>
      all.filter(
        (i) =>
          i.plannedFrom !== null &&
          i.plannedFrom <= `${year}-12` &&
          (i.plannedTo ?? i.plannedFrom) >= `${year}-01`,
      ),
    [all, year],
  );

  const elsewhere = all.length - inYear.length - unscheduled.length;

  /** Rows grouped by project, in the order the projects are listed. */
  const rows = useMemo<Row[]>(() => {
    const buckets: Row[] = (groups.data ?? []).map((group) => ({
      groupId: group.id,
      name: group.name,
      color: group.color,
      items: [],
      total: 0,
    }));
    buckets.push({
      groupId: null,
      name: 'Ohne Gruppe',
      color: UNGROUPED_COLOR,
      items: [],
      total: 0,
    });
    for (const investment of inYear) {
      const bucket = buckets.find((b) => b.groupId === investment.groupId) ?? buckets.at(-1)!;
      bucket.items.push(investment);
      if (investment.status !== 'cancelled') bucket.total += investment.effectiveCost;
    }
    return buckets.filter((bucket) => bucket.items.length > 0);
  }, [groups.data, inYear]);

  /** A window's cost counts in the month it starts – the same rule the budget uses. */
  const monthTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const investment of inYear) {
      if (investment.status === 'cancelled') continue;
      if (!investment.plannedFrom?.startsWith(String(year))) continue;
      totals.set(
        investment.plannedFrom,
        (totals.get(investment.plannedFrom) ?? 0) + investment.effectiveCost,
      );
    }
    return totals;
  }, [inYear, year]);

  const yearTotal = [...monthTotals.values()].reduce((sum, value) => sum + value, 0);
  /** The budget period covering this calendar year, when there is one. */
  const period = summary.data?.budget.periods.find(
    (candidate) => candidate.startDate.slice(0, 4) === String(year),
  );

  const onDragStart = ({ active }: DragStartEvent) => {
    const drag = active.data.current as CalendarDrag;
    const investment = all.find((i) => i.id === drag.investmentId);
    setDragging({ drag, title: investment?.title ?? '', group: investment?.groupName ?? null });
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setDragging(null);
    const drag = active.data.current as CalendarDrag | undefined;
    const investment = drag && all.find((i) => i.id === drag.investmentId);
    if (!drag || !investment || !over) return;
    const next = computeDrop(drag, String(over.id), investment);
    if (!next) return;
    setOverrides((current) => ({ ...current, [investment.id]: next }));
    updateInvestment.mutate(
      { id: investment.id, ...next },
      {
        onSettled: () =>
          setOverrides((current) => {
            const { [investment.id]: _done, ...rest } = current;
            return rest;
          }),
      },
    );
  };

  const open = (id: string) => {
    setDialogId(id);
    setDialogOpen(true);
  };

  return (
    <>
      <PageHeader
        title="Jahresplan"
        subtitle="Geplant wird in Monaten – ein Balken je Vorhaben über das ganze Jahr."
        actions={
          <>
            <div className="flex items-center gap-1 rounded-lg border border-[var(--border)] bg-[var(--surface-2)] p-0.5">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Vorheriges Jahr"
                onClick={() => setYear((current) => current - 1)}
              >
                <ChevronLeft size={16} />
              </Button>
              <span className="num min-w-[4rem] text-center text-sm font-medium">{year}</span>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Nächstes Jahr"
                onClick={() => setYear((current) => current + 1)}
              >
                <ChevronRight size={16} />
              </Button>
            </div>
            <Button onClick={() => setYear(yearOf(thisMonth))}>Heute</Button>
            {canEdit ? (
              <Button
                variant="primary"
                onClick={() => {
                  setDialogId(null);
                  setDialogOpen(true);
                }}
              >
                <Plus size={15} />
                Neue Investition
              </Button>
            ) : null}
          </>
        }
      />

      <QueryState
        isLoading={investments.isLoading}
        error={(investments.error as Error | null) ?? null}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={dropUnderPointer}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={() => setDragging(null)}
        >
          <div className="flex flex-col gap-5">
            <Card className="overflow-hidden">
              <CardHeader
                title={`Vorhaben ${year}`}
                subtitle={
                  period
                    ? `Budget ${formatMoneyShort(period.amount, currency)} – ${period.fittingCount} von ${period.investmentCount} passen`
                    : 'Für dieses Jahr ist kein Budget gesetzt'
                }
                action={
                  <span className="num text-sm font-semibold">
                    {formatMoneyShort(yearTotal, currency)}
                  </span>
                }
              />

              {period ? (
                <div className="px-5 pb-4">
                  <BudgetMeter
                    planned={period.planned}
                    budget={period.amount}
                    currency={currency}
                  />
                </div>
              ) : null}

              {rows.length === 0 ? (
                <EmptyState
                  icon={<CalendarRange size={22} />}
                  title={`Nichts für ${year} geplant`}
                  description={
                    canEdit
                      ? 'Zieh ein Vorhaben aus „Noch ohne Termin" auf einen Monat – oder blättere in ein anderes Jahr.'
                      : 'Blättere in ein anderes Jahr.'
                  }
                />
              ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-[40rem] px-3 pb-2">
                    {/* Month header */}
                    <div
                      className={`grid ${COLS} border-b border-[var(--border)] pb-1.5 text-[11px] text-[var(--ink-3)]`}
                    >
                      <div className="pl-1">Vorhaben</div>
                      {months.map((month, index) => (
                        <div
                          key={month}
                          title={formatMonth(month)}
                          className={[
                            'text-center',
                            month === thisMonth ? 'font-semibold text-[var(--accent)]' : '',
                          ].join(' ')}
                        >
                          {MONTH_INITIALS[index]}
                        </div>
                      ))}
                    </div>

                    {/* Rows, with the droppable month strips behind them */}
                    <div className="relative">
                      <div className={`absolute inset-0 grid ${COLS}`} aria-hidden="true">
                        <div />
                        {months.map((month) => (
                          <MonthColumn key={month} month={month} isNow={month === thisMonth} />
                        ))}
                      </div>

                      <div className="relative">
                        {rows.map((row) => (
                          <div key={row.groupId ?? 'none'}>
                            <div
                              className={`grid ${COLS} items-center pt-2.5 pb-0.5`}
                            >
                              <div className="flex items-center gap-2 pl-1">
                                <GroupBadge name={row.name} color={row.color} />
                              </div>
                              <div className="num col-span-12 pr-1 text-right text-[11px] text-[var(--ink-3)]">
                                {formatMoneyShort(row.total, currency)}
                              </div>
                            </div>

                            {row.items.map((investment) => {
                              const color = chartColor(
                                investment.groupColor ?? UNGROUPED_COLOR,
                                isDark,
                              );
                              const last = investment.plannedTo ?? investment.plannedFrom;
                              const label = `${
                                investment.groupName ? `${investment.groupName} › ` : ''
                              }${investment.title} · ${formatMonthRange(
                                investment.plannedFrom,
                                investment.plannedTo,
                              )} · ${formatMoney(investment.effectiveCost, currency)}`;
                              return (
                                <div
                                  key={investment.id}
                                  className={`grid ${COLS} items-center`}
                                >
                                  <button
                                    type="button"
                                    onClick={() => open(investment.id)}
                                    className="min-w-0 rounded-md py-1 pl-1 text-left transition-colors hover:bg-[var(--surface-2)]"
                                  >
                                    <span className="block truncate text-xs font-medium">
                                      {investment.title}
                                    </span>
                                    <span className="num block truncate text-[10px] text-[var(--ink-3)]">
                                      {formatMonthRange(
                                        investment.plannedFrom,
                                        investment.plannedTo,
                                      )}{' '}
                                      · {formatMoneyShort(investment.effectiveCost, currency)}
                                    </span>
                                  </button>
                                  {months.map((month) =>
                                    coversMonth(
                                      month,
                                      investment.plannedFrom,
                                      investment.plannedTo,
                                    ) ? (
                                      <PlanBar
                                        key={month}
                                        investmentId={investment.id}
                                        month={month}
                                        canEdit={canEdit}
                                        color={color}
                                        isStart={month === investment.plannedFrom}
                                        isEnd={month === last}
                                        onOpen={() => open(investment.id)}
                                        title={label}
                                      />
                                    ) : (
                                      <div key={month} />
                                    ),
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Month totals – a window counts in the month it starts */}
                    <div
                      className={`grid ${COLS} mt-2 border-t border-[var(--border)] pt-1.5 text-[10px]`}
                    >
                      <div className="pl-1 text-[var(--ink-3)]">Summe (Start)</div>
                      {months.map((month) => {
                        const total = monthTotals.get(month) ?? 0;
                        return (
                          <div
                            key={month}
                            title={`${formatMonth(month)}: ${formatMoneyShort(total, currency)}`}
                            className={[
                              'num text-center',
                              total > 0 ? 'font-medium text-[var(--ink-2)]' : 'text-[var(--ink-3)]',
                            ].join(' ')}
                          >
                            {total > 0 ? Math.round(total / 100_000) : '·'}
                          </div>
                        );
                      })}
                    </div>
                    <p className="px-1 pt-1 text-[10px] text-[var(--ink-3)]">
                      Summen in Tsd. {currency === 'EUR' ? '€' : currency}
                    </p>
                  </div>
                </div>
              )}

              {canEdit ? (
                <p className="border-t border-[var(--border)] px-4 py-2 text-[11px] text-[var(--ink-3)]">
                  Tipp: Einen Balken auf einen anderen Monat ziehen verschiebt das ganze Vorhaben –
                  an den Enden ziehen ändert nur den ersten bzw. letzten Monat.
                </p>
              ) : null}
            </Card>

            {elsewhere > 0 ? (
              <p className="px-1 text-xs text-[var(--ink-3)]">
                {elsewhere} Vorhaben {elsewhere === 1 ? 'ist' : 'sind'} in einem anderen Jahr
                geplant.
              </p>
            ) : null}

            {unscheduled.length > 0 || (dragging && dragging.drag.kind === 'move') ? (
              <UnscheduleZone active={dragging?.drag.kind === 'move'}>
                <Card>
                  <CardHeader
                    title="Noch ohne Termin"
                    subtitle={
                      canEdit
                        ? 'Auf einen Monat ziehen – oder hierher ziehen, um den Termin zu entfernen'
                        : 'Noch nicht terminiert'
                    }
                    action={
                      <span className="num text-sm font-semibold">
                        {formatMoneyShort(
                          unscheduled
                            .filter((investment) => investment.status !== 'cancelled')
                            .reduce((sum, investment) => sum + investment.effectiveCost, 0),
                          currency,
                        )}
                      </span>
                    }
                  />
                  <ul className="grid gap-1 px-2 pb-3 sm:grid-cols-2">
                    {unscheduled.map((investment) => (
                      <DraggableEntry
                        key={investment.id}
                        canEdit={canEdit}
                        drag={{
                          id: `list-place:${investment.id}`,
                          kind: 'place',
                          investmentId: investment.id,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => open(investment.id)}
                          className="flex w-full items-start justify-between gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-[var(--surface-2)]"
                        >
                          <span className="min-w-0">
                            <span className="block truncate text-sm">{investment.title}</span>
                            <span className="mt-1 flex flex-wrap items-center gap-1.5">
                              {investment.groupName ? (
                                <GroupBadge
                                  name={investment.groupName}
                                  color={investment.groupColor ?? UNGROUPED_COLOR}
                                />
                              ) : null}
                              <StatusBadge status={investment.status} />
                            </span>
                          </span>
                          <span className="num shrink-0 text-sm text-[var(--ink-2)]">
                            {formatMoney(investment.effectiveCost, currency)}
                          </span>
                        </button>
                      </DraggableEntry>
                    ))}
                  </ul>
                </Card>
              </UnscheduleZone>
            ) : null}
          </div>

          <DragOverlay dropAnimation={null}>
            {dragging ? (
              <div className="pointer-events-none max-w-[14rem] rounded-md bg-[var(--surface)] px-2 py-1 shadow-lg ring-1 ring-[var(--border)]">
                <span className="block truncate text-xs font-medium text-[var(--ink)]">
                  {dragging.drag.kind === 'start'
                    ? '⇤ Erster Monat: '
                    : dragging.drag.kind === 'end'
                      ? 'Letzter Monat ⇥: '
                      : ''}
                  {dragging.title}
                </span>
                {dragging.group ? (
                  <span className="block truncate text-[10px] text-[var(--ink-3)]">
                    {dragging.group}
                  </span>
                ) : null}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </QueryState>

      <InvestmentDialog
        open={dialogOpen}
        investmentId={dialogId}
        groups={groups.data ?? []}
        currency={currency}
        onClose={() => {
          setDialogOpen(false);
          setDialogId(null);
        }}
      />
    </>
  );
}
